import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Zap, CheckCircle2, User, MapPin, Camera, AlertTriangle, Printer, MessageCircle, QrCode, Copy, UserX, DoorOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { maskCPF, maskPhone, maskCEP, maskDate, isoFromBRDate, lookupCEP, unmask } from "@/lib/masks";
import { loadFaceModels, detectFaceDescriptor } from "@/lib/faceapi";
import MpPixDialog from "@/components/MpPixDialog";

const emptyForm = () => ({
  name: "", cpf: "", phone: "", email: "", birthdate: "", gender: "",
  zip_code: "", address: "", address_number: "", address_complement: "",
  neighborhood: "", city: "", state: "",
});

export default function QuickSale() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [units, setUnits] = useState([]);
  const [adminCfg, setAdminCfg] = useState(null);
  const [pixOpen, setPixOpen] = useState(false);
  const [anonOpen, setAnonOpen] = useState(false);
  const [anonPlan, setAnonPlan] = useState("");
  const [anonMethod, setAnonMethod] = useState("cash");
  const [anonLoading, setAnonLoading] = useState(false);
  const [anonReleasing, setAnonReleasing] = useState(false);
  const [anonReleased, setAnonReleased] = useState(false);
  const [anonResult, setAnonResult] = useState(null);
  const [anonMpPixOpen, setAnonMpPixOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [plan_id, setPlanId] = useState("");
  const [method, setMethod] = useState("cash"); // cash|card|pix_maquineta|pix_qrcode|manual
  const [split, setSplit] = useState(false);
  const [splitMethod, setSplitMethod] = useState("pix");
  const [splitAmount, setSplitAmount] = useState("");
  const [unit_id, setUnitId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [cepLoading, setCepLoading] = useState(false);
  const [cpfCheck, setCpfCheck] = useState(null);
  const [captured, setCaptured] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [modelsReady, setModelsReady] = useState(false);
  const [faceSaving, setFaceSaving] = useState(false);
  const [faceSkipped, setFaceSkipped] = useState(false);
  const [mpCharge, setMpCharge] = useState(null); // { external_reference, qr_code, qr_code_base64, amount, ticket_url, status }
  const [mpCreating, setMpCreating] = useState(false);
  const [mpSyncing, setMpSyncing] = useState(false);
  const mpPollRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const cpfTimer = useRef(null);

  useEffect(() => {
    Promise.all([api.get("/plans"), api.get("/units")]).then(([p, u]) => {
      setPlans(p.data); setUnits(u.data);
    });
    api.get("/admin-config").then(({ data }) => setAdminCfg(data)).catch(()=>{});
  }, []);
  useEffect(() => {
    if (result && !modelsReady) loadFaceModels().then(()=>setModelsReady(true)).catch(()=>toast.error("Erro carregando modelos faciais"));
  }, [result, modelsReady]);
  useEffect(() => () => { streamRef.current?.getTracks().forEach(t=>t.stop()); }, []);

  const onCepChange = async (val) => {
    const masked = maskCEP(val);
    setForm((f) => ({ ...f, zip_code: masked }));
    if (unmask(masked).length === 8) {
      setCepLoading(true);
      const found = await lookupCEP(masked); setCepLoading(false);
      if (found) {
        setForm((f) => ({ ...f, address: found.address || f.address, neighborhood: found.neighborhood || f.neighborhood, city: found.city || f.city, state: found.state || f.state }));
        toast.success("Endereço preenchido");
      } else toast.error("CEP não encontrado");
    }
  };

  const onCpfChange = (v) => {
    const m = maskCPF(v);
    setForm((f) => ({ ...f, cpf: m }));
    setCpfCheck(null);
    if (cpfTimer.current) clearTimeout(cpfTimer.current);
    if (unmask(m).length === 11) {
      cpfTimer.current = setTimeout(async () => {
        try {
          const { data } = await api.get(`/students/check-cpf/${unmask(m)}`);
          setCpfCheck(data);
          if (data.exists) toast.error(`CPF já cadastrado para ${data.student_name}`);
        } catch {}
      }, 400);
    }
  };

  const startCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "user" },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setStreaming(true); // dispara render do <video>, srcObject atribuído no useEffect abaixo
    } catch (e) {
      toast.error("Sem acesso à câmera" + (e?.message ? `: ${e.message}` : ""));
    }
  };
  // Atribui o stream ao <video> SÓ depois dele estar no DOM (fix mobile tela preta)
  useEffect(() => {
    if (streaming && streamRef.current && videoRef.current) {
      const v = videoRef.current;
      try { v.srcObject = streamRef.current; } catch {}
      v.muted = true;
      v.setAttribute("playsinline", "true");
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, [streaming]);
  const stopCam = () => { streamRef.current?.getTracks().forEach(t=>t.stop()); setStreaming(false); };
  const capture = async () => {
    const v = videoRef.current, c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const dataUrl = c.toDataURL("image/jpeg", 0.7);
    const desc = await detectFaceDescriptor(v);
    if (!desc) return toast.error("Rosto não detectado");
    setCaptured({ photo: dataUrl, descriptor: desc.descriptor });
    stopCam(); toast.success("Rosto capturado (128D)");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.cpf || !plan_id) return toast.error("Preencha nome, CPF e plano");
    if (cpfCheck?.exists) return toast.error("Este CPF já está cadastrado — não é possível prosseguir");
    // "Pix com QR Code na Tela" agora usa Mercado Pago real
    if (method === "pix_qrcode" && !mpCharge) {
      await createMpPix();
      return;
    }
    setLoading(true);
    try {
      const student = { ...form, unit_id: unit_id || undefined };
      if (student.birthdate) student.birthdate = isoFromBRDate(student.birthdate) || student.birthdate;
      // Face será capturada APÓS confirmação do pagamento
      // Both pix variants map to "pix" in the backend for financial reporting
      const apiMethod = (method === "pix_maquineta" || method === "pix_qrcode") ? "pix" : method;
      const payload = { student, plan_id, payment_method: apiMethod, mark_paid: true };
      const planObj = plans.find(p => p.id === plan_id);
      const splitAmt = parseFloat(splitAmount) || 0;
      if (split && planObj) {
        if (splitMethod === apiMethod) { setLoading(false); return toast.error("A 2ª forma precisa ser diferente da primeira"); }
        if (splitAmt <= 0 || splitAmt >= planObj.price) { setLoading(false); return toast.error(`Valor da 2ª forma deve estar entre R$ 0,01 e R$ ${(planObj.price - 0.01).toFixed(2)}`); }
        payload.split_method = splitMethod;
        payload.split_amount = splitAmt;
      }
      const { data } = await api.post("/quick-sale", payload);
      setResult({ ...data, ui_method: method, split: split ? { method: splitMethod, amount: splitAmt } : null });
      setPixOpen(false);
      toast.success(split ? "Aluno cadastrado e pagamento dividido registrado!" : "Aluno cadastrado e plano vendido!");
    } catch (e) {
      if (e.response?.status === 423 && e.response?.data?.detail?.code === "cash_closed") {
        const info = e.response.data.detail;
        const reason = window.prompt(
          `⚠️ CAIXA FECHADO\n\nO caixa de hoje (${info.date}) foi fechado por ${info.closed_by_name || "—"}.\n\nPara registrar essa venda, é preciso reabrir o caixa.\nInforme o MOTIVO (ficará no histórico de auditoria):`,
          ""
        );
        if (reason === null) { setLoading(false); return; }
        const r = (reason || "").trim();
        if (!r) { setLoading(false); return toast.error("Motivo obrigatório"); }
        try {
          const q = new URLSearchParams({ date: info.date, reason: r, ...(info.unit_id ? { unit_id: info.unit_id } : {}) }).toString();
          await api.post(`/finance/reopen-day?${q}`);
          toast.success("Caixa reaberto — refazendo venda...");
          // Retry a venda recursivamente
          setLoading(false);
          return submit(e);
        } catch (err) {
          toast.error(err.response?.data?.detail || "Falha ao reabrir caixa");
        }
      } else {
        toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Erro");
      }
    }
    finally { setLoading(false); }
  };

  const reset = () => {
    setForm(emptyForm()); setPlanId(""); setMethod("cash"); setResult(null); setCpfCheck(null);
    setCaptured(null); setFaceSkipped(false); setSplit(false); setSplitAmount(""); setSplitMethod("pix");
    if (mpPollRef.current) { clearInterval(mpPollRef.current); mpPollRef.current = null; }
    setMpCharge(null); setPixOpen(false);
  };

  const saveFaceToStudent = async () => {
    if (!captured || !result?.student?.id) return;
    setFaceSaving(true);
    try {
      await api.patch(`/students/${result.student.id}`, {
        face_photo: captured.photo,
        face_embedding: captured.descriptor,
      });
      toast.success("Rosto cadastrado — já libera a catraca!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar rosto");
      setCaptured(null);
    } finally { setFaceSaving(false); }
  };

  const createMpPix = async () => {
    if (!plan_id) return toast.error("Selecione o plano");
    const planObj = plans.find(p => p.id === plan_id);
    if (!planObj) return toast.error("Plano inválido");
    setMpCreating(true);
    try {
      const student = { ...form, unit_id: unit_id || undefined };
      if (student.birthdate) student.birthdate = isoFromBRDate(student.birthdate) || student.birthdate;
      // Face será capturada APÓS confirmação do pagamento
      // Marca como pendente de pagamento para o webhook criar o aluno depois do Pix confirmado
      student.payment_status = "pending";
      student.active = true;
      student.plan_id = plan_id;
      const payload = {
        plan_id,
        payer_email: form.email || undefined,
        pending_student: student,
      };
      const { data } = await api.post("/payments/mp/pix/create", payload);
      setMpCharge({ ...data });
      setPixOpen(true);
      // Inicia polling de status (a cada 3s)
      if (mpPollRef.current) clearInterval(mpPollRef.current);
      mpPollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/payments/mp/status/${data.external_reference}`);
          if (st.status === "paid") {
            clearInterval(mpPollRef.current); mpPollRef.current = null;
            setMpCharge((c) => c ? { ...c, status: "paid" } : null);
            // Fecha modal, mostra sucesso
            setPixOpen(false);
            setResult({ student: { ...student, id: st.student_id }, plan: planObj, ui_method: "pix_qrcode", mp: { paid_at: st.paid_at, mp_payment_id: st.mp_payment_id, amount: st.amount } });
            toast.success("Pagamento Pix confirmado via Mercado Pago!");
          }
        } catch (e) { /* ignora */ }
      }, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar Pix Mercado Pago"); }
    finally { setMpCreating(false); }
  };

  const cancelMpPix = () => {
    if (mpPollRef.current) { clearInterval(mpPollRef.current); mpPollRef.current = null; }
    setPixOpen(false);
    const c = mpCharge;
    setMpCharge(null);
    if (!c || c.status === "paid") return;
    api.post(`/payments/mp/pix/cancel/${c.external_reference}`).then(({ data }) => {
      if (data?.status === "paid") toast.success("O Pix já tinha sido pago — confira o financeiro do aluno.");
      else toast.info("Cobrança Pix cancelada no Mercado Pago");
    }).catch(() => {});
  };

  const forceMpSync = async () => {
    if (!mpCharge) return;
    setMpSyncing(true);
    try {
      const { data } = await api.post(`/payments/mp/sync/${mpCharge.external_reference}`);
      if (data.status === "paid") {
        if (mpPollRef.current) { clearInterval(mpPollRef.current); mpPollRef.current = null; }
        setPixOpen(false);
        const planObj = plans.find(p => p.id === plan_id);
        // Busca student_id via status endpoint (webhook/apply já criou o aluno)
        let studentId = null;
        try {
          const { data: st } = await api.get(`/payments/mp/status/${mpCharge.external_reference}`);
          studentId = st.student_id;
        } catch {}
        setResult({ student: { ...form, id: studentId }, plan: planObj, ui_method: "pix_qrcode" });
        toast.success("Pagamento confirmado!");
      } else {
        toast.info(`Status atual: ${data.mp_order_status || data.status}`);
      }
    } catch (e) { toast.error("Erro ao consultar MP"); }
    finally { setMpSyncing(false); }
  };

  const submitAnonymous = async () => {
    if (!anonPlan) return toast.error("Selecione o plano");
    // Pix QR Code na Tela → Mercado Pago (confirmação automática)
    if (anonMethod === "pix_qrcode") {
      setAnonMpPixOpen(true);
      return;
    }
    // Both pix variants map to "pix" in the backend
    const apiMethod = anonMethod === "pix_maquineta" ? "pix" : anonMethod;
    setAnonLoading(true);
    try {
      const { data } = await api.post("/quick-sale/anonymous", { plan_id: anonPlan, payment_method: apiMethod });
      setAnonResult({ ...data, ui_method: anonMethod });
      toast.success("Venda sem cadastro registrada!");
    } catch (e) {
      if (e.response?.status === 423 && e.response?.data?.detail?.code === "cash_closed") {
        const info = e.response.data.detail;
        const reason = window.prompt(
          `⚠️ CAIXA FECHADO\n\nO caixa de hoje (${info.date}) foi fechado por ${info.closed_by_name || "—"}.\n\nPara registrar essa venda sem cadastro, é preciso reabrir o caixa.\nInforme o MOTIVO (ficará no histórico de auditoria):`,
          ""
        );
        if (reason === null) { setAnonLoading(false); return; }
        const r = (reason || "").trim();
        if (!r) { setAnonLoading(false); return toast.error("Motivo obrigatório"); }
        try {
          const q = new URLSearchParams({ date: info.date, reason: r, ...(info.unit_id ? { unit_id: info.unit_id } : {}) }).toString();
          await api.post(`/finance/reopen-day?${q}`);
          toast.success("Caixa reaberto — refazendo venda...");
          setAnonLoading(false);
          return submitAnonymous();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Falha ao reabrir caixa");
        }
      } else {
        toast.error(e.response?.data?.detail || "Erro");
      }
    }
    finally { setAnonLoading(false); }
  };
  const onAnonMpPaid = (chargeInfo) => {
    const planObj = plans.find(p => p.id === anonPlan);
    setAnonResult({
      session_id: `mp_${chargeInfo?.external_reference || ""}`,
      plan: planObj,
      amount: chargeInfo?.amount || planObj?.price,
      payment_method: "pix",
      ui_method: "pix_qrcode",
      created_at: new Date().toISOString(),
    });
    toast.success("Pagamento Pix confirmado via Mercado Pago!");
  };
  const closeAnon = () => {
    setAnonOpen(false);
    const firstDaily = plans.find(p => p.duration_days && p.duration_days > 0);
    setAnonPlan(firstDaily?.id || "");
    setAnonMethod("cash"); setAnonResult(null);
    setAnonReleased(false);
    setAnonMpPixOpen(false);
  };
  const releaseAnonTurnstile = async () => {
    if (!anonResult) return;
    setAnonReleasing(true);
    try {
      await api.post("/turnstile/general-release", { note: `Venda sem cadastro — ${anonResult.plan.name} (R$ ${anonResult.plan.price.toFixed(2)}) · sessão ${anonResult.session_id}` });
      setAnonReleased(true);
      toast.success("Catraca liberada!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao liberar"); }
    finally { setAnonReleasing(false); }
  };
  const printAnonReceipt = () => {
    if (!anonResult) return;
    const p = anonResult.plan;
    const unit = units.find(u => u.id === unit_id) || null;
    const unitName = unit?.name || "";
    const unitRazao = unit?.razao_social || "";
    const unitCnpj = unit?.cnpj || "";
    const unitAddr = unit?.address || "";
    const unitPhone = unit?.phone || "";
    const methodLabel = {
      cash: "DINHEIRO", card: "CARTÃO (MAQUINETA)",
      pix_maquineta: "PIX (MAQUINETA)", pix_qrcode: "PIX (QR CODE)",
      pix: "PIX", manual: "MANUAL",
    }[anonResult.ui_method || anonMethod] || anonMethod;
    const now = new Date();
    const receiptNum = (anonResult.session_id || Date.now().toString()).slice(-8).toUpperCase();
    const amount = p.price.toFixed(2).replace(".", ",");
    const duration = p.duration_days ? `${p.duration_days} dia(s)` : `${p.duration_months} mês(es)`;
    const esc = (v) => String(v ?? "").replace(/[<>]/g, "");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Recibo — Sem Cadastro</title>
    <style>
      @page { size: 80mm auto; margin: 0; }
      * { box-sizing: border-box; }
      body { font-family: 'Courier New', monospace; width: 80mm; margin: 0; padding: 6mm 4mm; color: #000; font-size: 11px; line-height: 1.35; }
      .center { text-align: center; }
      .brand { font-size: 14px; font-weight: 900; letter-spacing: 1px; }
      .sub { font-size: 10px; }
      .divider { border-top: 1px dashed #000; margin: 6px 0; }
      .row { display: flex; justify-content: space-between; gap: 6px; font-size: 11px; }
      .row .k { text-transform: uppercase; }
      .row .v { font-weight: 700; text-align: right; }
      .title { font-size: 12px; font-weight: 900; text-transform: uppercase; margin: 4px 0; }
      .total { text-align: center; margin: 8px 0; }
      .total .lbl { font-size: 10px; text-transform: uppercase; }
      .total .val { font-size: 20px; font-weight: 900; }
      .foot { text-align: center; font-size: 10px; margin-top: 8px; }
      .sign-wrap { margin-top: 18px; text-align: center; font-size: 10px; }
      .sign-line { border-top: 1px solid #000; margin: 0 6mm 2px 6mm; }
      @media print { body { padding: 4mm 3mm; } }
    </style></head><body>
      <div class="center">
        <div class="brand">AMERICAN FIT</div>
        ${unitName ? `<div class="sub">${esc(unitName)}</div>` : ""}
        ${unitRazao ? `<div class="sub" style="font-weight:700">${esc(unitRazao)}</div>` : ""}
        ${unitCnpj ? `<div class="sub">CNPJ: ${esc(unitCnpj)}</div>` : ""}
        ${unitAddr ? `<div class="sub">${esc(unitAddr)}</div>` : ""}
        ${unitPhone ? `<div class="sub">Tel: ${esc(unitPhone)}</div>` : ""}
        <div class="divider"></div>
        <div class="sub">RECIBO DE PAGAMENTO</div>
        <div class="sub">Nº ${receiptNum}</div>
        <div class="sub">${now.toLocaleString("pt-BR")}</div>
      </div>
      <div class="divider"></div>
      <div class="title">DADOS DO CLIENTE</div>
      <div class="row"><span class="k">Nome</span><span class="v">SEM CADASTRO</span></div>
      <div class="divider"></div>
      <div class="title">SERVIÇO</div>
      <div class="row"><span class="k">Plano</span><span class="v">${esc(p.name)}</span></div>
      <div class="row"><span class="k">Duração</span><span class="v">${duration}</span></div>
      <div class="row"><span class="k">Pagamento</span><span class="v">${methodLabel}</span></div>
      <div class="row"><span class="k">Status</span><span class="v">Pago</span></div>
      <div class="divider"></div>
      <div class="total">
        <div class="lbl">VALOR PAGO</div>
        <div class="val">R$ ${amount}</div>
      </div>
      <div class="divider"></div>
      <div class="sign-wrap">
        <div style="height:28px"></div>
        <div class="sign-line"></div>
        <div>Assinatura do responsável</div>
        ${unitRazao ? `<div style="margin-top:2px">${esc(unitRazao)}</div>` : ""}
      </div>
      <div class="foot">Obrigado pela preferência!</div>
      <div class="foot">Guarde este comprovante.</div>
      <script>window.onload=()=>setTimeout(()=>{window.print();setTimeout(()=>window.close(),500);},250);</script>
    </body></html>`;
    const w = window.open("", "_blank", "width=380,height=700");
    if (!w) return toast.error("Popup bloqueado");
    w.document.write(html); w.document.close();
  };

  const plan = plans.find(p => p.id === plan_id);
  const unitName = units.find(u => u.id === (result?.student?.unit_id || unit_id))?.name;

  const printReceipt = () => {
    if (!result) return;
    const s = result.student, p = result.plan;
    const unit = units.find(u => u.id === (s.unit_id || unit_id)) || null;
    const unitName = unit?.name || "";
    const unitRazao = unit?.razao_social || "";
    const unitCnpj = unit?.cnpj || "";
    const unitAddr = unit?.address || "";
    const unitPhone = unit?.phone || "";
    const methodLabel = {
      cash: "DINHEIRO", card: "CARTÃO (MAQUINETA)",
      pix_maquineta: "PIX (MAQUINETA)", pix_qrcode: "PIX (QR CODE)",
      pix: "PIX", manual: "MANUAL",
    }[result.ui_method || method] || (result.ui_method || method);
    const now = new Date();
    const receiptNum = (result.session_id || Date.now().toString()).slice(-8).toUpperCase();
    const validUntil = result.membership_end ? new Date(result.membership_end) : null;
    const amount = p.price.toFixed(2).replace(".", ",");
    const duration = p.duration_days ? `${p.duration_days} dia(s)` : `${p.duration_months} mês(es)`;
    const esc = (v) => String(v ?? "").replace(/[<>]/g, "");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Recibo — ${esc(s.name)}</title>
    <style>
      @page { size: 80mm auto; margin: 0; }
      * { box-sizing: border-box; }
      body { font-family: 'Courier New', monospace; width: 80mm; margin: 0; padding: 6mm 4mm; color: #000; font-size: 11px; line-height: 1.35; }
      .center { text-align: center; }
      .brand { font-size: 14px; font-weight: 900; letter-spacing: 1px; }
      .sub { font-size: 10px; }
      .divider { border-top: 1px dashed #000; margin: 6px 0; }
      .row { display: flex; justify-content: space-between; gap: 6px; font-size: 11px; }
      .row .k { text-transform: uppercase; }
      .row .v { font-weight: 700; text-align: right; }
      .title { font-size: 12px; font-weight: 900; text-transform: uppercase; margin: 4px 0; }
      .total { text-align: center; margin: 8px 0; }
      .total .lbl { font-size: 10px; text-transform: uppercase; }
      .total .val { font-size: 20px; font-weight: 900; }
      .foot { text-align: center; font-size: 10px; margin-top: 8px; }
      .sign-wrap { margin-top: 18px; text-align: center; font-size: 10px; }
      .sign-line { border-top: 1px solid #000; margin: 0 6mm 2px 6mm; }
      @media print { body { padding: 4mm 3mm; } }
    </style></head><body>
      <div class="center">
        <div class="brand">AMERICAN FIT</div>
        ${unitName ? `<div class="sub">${esc(unitName)}</div>` : ""}
        ${unitRazao ? `<div class="sub" style="font-weight:700">${esc(unitRazao)}</div>` : ""}
        ${unitCnpj ? `<div class="sub">CNPJ: ${esc(unitCnpj)}</div>` : ""}
        ${unitAddr ? `<div class="sub">${esc(unitAddr)}</div>` : ""}
        ${unitPhone ? `<div class="sub">Tel: ${esc(unitPhone)}</div>` : ""}
        <div class="divider"></div>
        <div class="sub">RECIBO DE PAGAMENTO</div>
        <div class="sub">Nº ${receiptNum}</div>
        <div class="sub">${now.toLocaleString("pt-BR")}</div>
      </div>
      <div class="divider"></div>
      <div class="title">DADOS DO ALUNO</div>
      <div class="row"><span class="k">Nome</span><span class="v">${esc(s.name)}</span></div>
      ${s.cpf ? `<div class="row"><span class="k">CPF</span><span class="v">${esc(maskCPF(s.cpf))}</span></div>` : ""}
      ${s.phone ? `<div class="row"><span class="k">Tel</span><span class="v">${esc(maskPhone(s.phone))}</span></div>` : ""}
      <div class="divider"></div>
      <div class="title">SERVIÇO</div>
      <div class="row"><span class="k">Plano</span><span class="v">${esc(p.name)}</span></div>
      <div class="row"><span class="k">Duração</span><span class="v">${duration}</span></div>
      ${validUntil ? `<div class="row"><span class="k">Válido até</span><span class="v">${validUntil.toLocaleDateString("pt-BR")}</span></div>` : ""}
      <div class="row"><span class="k">Pagamento</span><span class="v">${methodLabel}</span></div>
      <div class="row"><span class="k">Status</span><span class="v">Pago</span></div>
      <div class="divider"></div>
      <div class="total">
        <div class="lbl">VALOR PAGO</div>
        <div class="val">R$ ${amount}</div>
      </div>
      <div class="divider"></div>
      <div class="sign-wrap">
        <div style="height:28px"></div>
        <div class="sign-line"></div>
        <div>Assinatura do responsável</div>
        ${unitRazao ? `<div style="margin-top:2px">${esc(unitRazao)}</div>` : ""}
      </div>
      <div class="foot">Obrigado pela preferência!</div>
      <div class="foot">Guarde este comprovante.</div>
      <script>window.onload=()=>setTimeout(()=>{window.print();setTimeout(()=>window.close(),500);},250);</script>
    </body></html>`;
    const w = window.open("", "_blank", "width=380,height=700");
    if (!w) return toast.error("Popup bloqueado");
    w.document.write(html); w.document.close();
  };
  const sendWhatsApp = () => {
    if (!result) return;
    const s = result.student, p = result.plan;
    const phone = (s.phone || "").replace(/\D/g, "");
    if (!phone) return toast.error("Aluno sem telefone cadastrado");
    const msg = `Olá ${s.name.split(" ")[0]}! 🎉%0A%0AObrigado por escolher a American Fit! Seu plano foi ativado:%0A%0A📋 *Plano:* ${p.name}%0A💰 *Valor:* R$ ${p.price.toFixed(2)}%0A📅 *Válido até:* ${new Date(result.membership_end).toLocaleDateString("pt-BR")}%0A%0ANos vemos na academia! 💪`;
    window.open(`https://wa.me/55${phone}?text=${msg}`, "_blank");
  };

  if (result) {
    return (
      <div className="max-w-lg mx-auto pt-4 sm:pt-8 px-2 sm:px-0">
        <Card className="p-5 sm:p-8 border border-emerald-200 bg-emerald-50 text-center" data-testid="quicksale-success">
          <CheckCircle2 className="w-16 h-16 sm:w-20 sm:h-20 text-emerald-600 mx-auto mb-4"/>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">Venda concluída!</h2>
          <p className="text-slate-700 mt-2 text-sm sm:text-base"><strong>{result.student.name}</strong> agora tem o plano <strong>{result.plan.name}</strong></p>
          <p className="text-xs sm:text-sm text-slate-600 mt-1">Válido até {new Date(result.membership_end).toLocaleDateString("pt-BR")}</p>
          <div className="font-heading text-2xl sm:text-3xl font-extrabold text-emerald-600 mt-4">R$ {result.plan.price.toFixed(2)}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-6">
            <Button onClick={printReceipt} className="bg-slate-900 hover:bg-black text-white font-bold uppercase h-12" data-testid="quicksale-print-receipt"><Printer className="w-4 h-4 mr-2"/>Imprimir recibo</Button>
            <Button onClick={sendWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase h-12" data-testid="quicksale-wa-receipt"><MessageCircle className="w-4 h-4 mr-2"/>WhatsApp</Button>
          </div>

          {/* ===== CAPTURA DE ROSTO PÓS-PAGAMENTO ===== */}
          {result.student?.id && !faceSkipped && (
            <div className="mt-6 p-4 bg-white rounded-xl border-2 border-rose-200 text-left" data-testid="quicksale-face-postpay">
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-5 h-5 text-rose-600"/>
                <div className="font-heading font-bold text-slate-900 uppercase tracking-wide">Capturar rosto para a catraca</div>
              </div>
              <p className="text-xs text-slate-500 mb-3">Opcional — libera o acesso facial automático. Pode pular e fazer depois no cadastro do aluno.</p>
              {captured ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <img src={captured.photo} className="w-20 h-20 rounded-lg object-cover border-2 border-emerald-500" alt=""/>
                    <div className="flex-1 text-sm text-emerald-700 font-semibold">Rosto detectado (128D) ✓</div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={()=>{ setCaptured(null); startCam(); }} className="flex-1" data-testid="quicksale-face-redo">Refazer</Button>
                    <Button type="button" size="sm" onClick={saveFaceToStudent} disabled={faceSaving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="quicksale-face-save">
                      {faceSaving ? "Salvando..." : "Confirmar e liberar catraca"}
                    </Button>
                  </div>
                </div>
              ) : streaming ? (
                <div className="space-y-2">
                  <video ref={videoRef} autoPlay playsInline muted className="w-full max-w-sm rounded-lg bg-black mx-auto" style={{ transform: "scaleX(-1)" }}/>
                  <canvas ref={canvasRef} className="hidden"/>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={capture} className="flex-1 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="quicksale-face-capture">Capturar</Button>
                    <Button type="button" size="sm" variant="outline" onClick={stopCam}>Cancelar</Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={startCam} className="flex-1 bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="quicksale-face-open">
                    {modelsReady ? (<><Camera className="w-4 h-4 mr-2"/>Abrir câmera</>) : "Carregando modelos..."}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={()=>setFaceSkipped(true)} className="text-slate-500" data-testid="quicksale-face-skip">
                    Pular por enquanto
                  </Button>
                </div>
              )}
            </div>
          )}
          {faceSkipped && !captured && (
            <div className="mt-4 text-xs text-slate-400 italic" data-testid="quicksale-face-skipped-msg">
              Captura facial pulada. Você pode cadastrar depois no perfil do aluno.
            </div>
          )}
          {/* ===== FIM CAPTURA ===== */}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
            <Button variant="outline" onClick={()=>navigate("/alunos")} className="h-11">Ver alunos</Button>
            <Button onClick={reset} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase h-11">Nova venda</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0 pb-24 lg:pb-0">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Zap className="w-6 h-6 sm:w-8 sm:h-8 text-rose-600"/>Venda Rápida</h1>
          <p className="text-slate-500 text-sm sm:text-base">Cadastre o aluno e venda o plano na mesma tela — ideal para o balcão</p>
        </div>
        <Button type="button" onClick={()=>{
          const firstDaily = plans.find(p => p.duration_days && p.duration_days > 0);
          if (firstDaily) setAnonPlan(firstDaily.id);
          setAnonOpen(true);
        }} data-testid="quicksale-anon-open" className="bg-slate-900 hover:bg-black text-white font-bold uppercase w-full sm:w-auto">
          <UserX className="w-4 h-4 mr-2"/>Venda sem cadastro
        </Button>
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Card className="p-4 sm:p-5 border border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-rose-600"/>
              <h3 className="font-heading font-bold text-slate-900">1. Dados do aluno</h3>
            </div>
            <div className="space-y-3">
              <div><Label>Nome completo *</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="quicksale-name-input" required/></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>CPF *</Label>
                  <Input value={form.cpf} onChange={(e)=>onCpfChange(e.target.value)} placeholder="000.000.000-00" inputMode="numeric" data-testid="quicksale-cpf-input" required
                    className={cpfCheck?.exists ? "border-rose-500 bg-rose-50" : ""}/>
                  {cpfCheck?.exists && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-rose-600 font-semibold" data-testid="quicksale-cpf-duplicate">
                      <AlertTriangle className="w-3 h-3"/>Já cadastrado: {cpfCheck.student_name}
                    </div>
                  )}
                </div>
                <div><Label>Telefone</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:maskPhone(e.target.value)})} placeholder="(11) 99999-9999" inputMode="tel"/></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} inputMode="email"/></div>
                <div><Label>Nascimento</Label><Input inputMode="numeric" value={form.birthdate} onChange={(e)=>setForm({...form,birthdate:maskDate(e.target.value)})} placeholder="DD/MM/AAAA" data-testid="quicksale-birthdate-input"/></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Gênero</Label>
                  <Select value={form.gender} onValueChange={(v)=>setForm({...form,gender:v})}>
                    <SelectTrigger><SelectValue placeholder="Selecionar"/></SelectTrigger>
                    <SelectContent><SelectItem value="Masculino">Masculino</SelectItem><SelectItem value="Feminino">Feminino</SelectItem><SelectItem value="Outro">Outro</SelectItem></SelectContent>
                  </Select>
                </div>
                {units.length > 0 && (
                  <div>
                    <Label>Unidade</Label>
                    <Select value={unit_id} onValueChange={setUnitId}>
                      <SelectTrigger><SelectValue placeholder="Padrão"/></SelectTrigger>
                      <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card className="p-4 sm:p-5 border border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-rose-600"/>
              <h3 className="font-heading font-bold text-slate-900">Endereço</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
              <div className="col-span-1 sm:col-span-3">
                <Label>CEP {cepLoading && <span className="text-xs text-sky-600 ml-1">buscando...</span>}</Label>
                <Input value={form.zip_code} onChange={(e)=>onCepChange(e.target.value)} placeholder="00000-000" inputMode="numeric" data-testid="quicksale-cep-input"/>
              </div>
              <div className="col-span-1 sm:col-span-3"><Label>Bairro</Label><Input value={form.neighborhood} onChange={(e)=>setForm({...form,neighborhood:e.target.value})}/></div>
              <div className="col-span-2 sm:col-span-4"><Label>Logradouro</Label><Input value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})} data-testid="quicksale-address-input"/></div>
              <div className="col-span-1 sm:col-span-2"><Label>Número</Label><Input value={form.address_number} onChange={(e)=>setForm({...form,address_number:e.target.value})} inputMode="numeric"/></div>
              <div className="col-span-2 sm:col-span-4"><Label>Cidade</Label><Input value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})}/></div>
              <div className="col-span-1 sm:col-span-1"><Label>UF</Label><Input maxLength={2} value={form.state} onChange={(e)=>setForm({...form,state:e.target.value.toUpperCase()})}/></div>
              <div className="col-span-1 sm:col-span-1"><Label>Compl.</Label><Input value={form.address_complement} onChange={(e)=>setForm({...form,address_complement:e.target.value})}/></div>
            </div>
          </Card>

          <Card className="p-4 sm:p-5 border border-slate-200">
            <div className="flex items-start gap-3">
              <Camera className="w-5 h-5 text-slate-400 mt-0.5 shrink-0"/>
              <div className="text-xs text-slate-500 leading-relaxed">
                <strong className="text-slate-700">Captura do rosto para a catraca</strong><br/>
                Aparecerá <strong>após a confirmação do pagamento</strong>. Assim que o plano for pago, você poderá tirar a foto do aluno e liberar o acesso facial.
              </div>
            </div>
          </Card>
          <canvas ref={canvasRef} className="hidden"/>
        </div>

        <Card className="p-4 sm:p-5 border border-slate-200 bg-gradient-to-br from-slate-50 to-white h-fit lg:sticky lg:top-4">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-rose-600"/>
            <h3 className="font-heading font-bold text-slate-900">2. Plano e pagamento</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label>Plano *</Label>
              <Select value={plan_id} onValueChange={setPlanId}>
                <SelectTrigger data-testid="quicksale-plan-select"><SelectValue placeholder="Selecionar"/></SelectTrigger>
                <SelectContent>
                  {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — R$ {p.price.toFixed(2)} ({p.duration_days ? `${p.duration_days} dia(s)` : `${p.duration_months} mês(es)`})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Forma de pagamento</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="quicksale-method-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Dinheiro</SelectItem>
                  <SelectItem value="card">Cartão (máquina)</SelectItem>
                  <SelectItem value="pix_maquineta" data-testid="method-pix-maquineta">Pix na Maquineta</SelectItem>
                  <SelectItem value="pix_qrcode" data-testid="method-pix-qrcode">Pix com QR Code na Tela</SelectItem>
                </SelectContent>
              </Select>
              {method === "pix_maquineta" && (
                <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">💳 Cobre o Pix na maquineta do cartão como faz normalmente. Nenhum QR será exibido na tela.</p>
              )}
              {method === "pix_qrcode" && (
                <p className="text-[11px] text-emerald-700 mt-1.5 leading-relaxed">📱 Um QR Code Pix aparecerá na tela para o aluno pagar direto do celular.</p>
              )}
            </div>

            {/* Pagamento dividido — visível sempre; campos ativam quando um plano é escolhido */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="quicksale-split-panel">
              <label className={`inline-flex items-center gap-2 ${plan ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}>
                <input type="checkbox" checked={split} disabled={!plan} onChange={(e)=>setSplit(e.target.checked)} className="w-4 h-4 accent-rose-600" data-testid="quicksale-split-toggle"/>
                <span className="font-heading text-sm font-bold text-slate-900 uppercase tracking-wider">Dividir pagamento</span>
                <span className="text-[11px] text-slate-500">{plan ? "Ex.: parte em dinheiro, parte no Pix" : "Escolha um plano primeiro"}</span>
              </label>
              {plan && split && (() => {
                const splitAmt = parseFloat(splitAmount) || 0;
                const remaining = Math.max(0, plan.price - splitAmt);
                const primaryLabel = { cash: "Dinheiro", card: "Cartão", pix_maquineta: "Pix Maquineta", pix_qrcode: "Pix QR Code", manual: "Manual" }[method];
                const secondaryLabel = { cash: "Dinheiro", card: "Cartão", pix: "Pix", manual: "Manual" }[splitMethod];
                return (
                  <div className="grid md:grid-cols-3 gap-3 mt-3">
                    <div>
                      <Label className="text-xs">2ª forma</Label>
                      <Select value={splitMethod} onValueChange={setSplitMethod}>
                        <SelectTrigger data-testid="quicksale-split-method"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Dinheiro</SelectItem>
                          <SelectItem value="card">Cartão</SelectItem>
                          <SelectItem value="pix">Pix</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Valor na 2ª forma (R$)</Label>
                      <Input type="number" step="0.01" min="0.01" max={plan.price - 0.01} value={splitAmount} onChange={(e)=>setSplitAmount(e.target.value)} placeholder="0,00" data-testid="quicksale-split-amount"/>
                    </div>
                    <div className="bg-white rounded-lg border border-slate-200 p-2 text-xs">
                      <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-mono font-bold text-slate-900">R$ {plan.price.toFixed(2)}</span></div>
                      <div className="flex justify-between mt-1"><span className="text-slate-500">{primaryLabel}</span><span className="font-mono font-bold text-emerald-600" data-testid="quicksale-split-primary-amount">R$ {remaining.toFixed(2)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">{secondaryLabel}</span><span className="font-mono font-bold text-rose-600">R$ {splitAmt.toFixed(2)}</span></div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {plan && (
              <div className="p-4 bg-white rounded-lg border border-rose-200 mt-4">
                <div className="text-xs uppercase tracking-wider text-slate-500">Total</div>
                <div className="font-heading text-3xl sm:text-4xl font-extrabold text-rose-600">R$ {plan.price.toFixed(2)}</div>
                <div className="text-xs text-slate-500 mt-1">Válido até {(() => {
                  if (plan.duration_days) {
                    const d = new Date(); d.setDate(d.getDate() + plan.duration_days - 1); d.setHours(23,59,59,0);
                    return d.toLocaleDateString("pt-BR");
                  }
                  return new Date(Date.now() + (plan.duration_months||0)*30*86400000).toLocaleDateString("pt-BR");
                })()}</div>
              </div>
            )}
            <Button type="submit" disabled={loading || cpfCheck?.exists} data-testid="quicksale-submit-button" className="w-full bg-rose-600 hover:bg-rose-700 text-base sm:text-lg py-5 sm:py-6 font-bold uppercase">
              {loading ? "Processando..." : method === "pix_qrcode" ? "Mostrar QR Pix e confirmar" : "Cadastrar e vender"}
            </Button>
          </div>
        </Card>
      </form>

      {/* Mercado Pago Pix dialog (QR real + polling) */}
      <Dialog open={pixOpen} onOpenChange={(v)=>{ if (!v) cancelMpPix(); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[95vh] overflow-y-auto" data-testid="quicksale-pix-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-base sm:text-lg"><QrCode className="w-5 h-5 text-rose-600"/>Pix via Mercado Pago</DialogTitle></DialogHeader>
          <div className="text-center space-y-3">
            {mpCreating && <div className="py-10 text-slate-500 text-sm">Gerando QR Code no Mercado Pago...</div>}
            {!mpCreating && mpCharge && (
              <>
                <p className="text-xs sm:text-sm text-slate-600">Peça ao aluno escanear o QR Code abaixo com o app do banco. O status atualiza sozinho em até 10 segundos após o pagamento.</p>
                {mpCharge.qr_code_base64 ? (
                  <img src={`data:image/png;base64,${mpCharge.qr_code_base64}`} alt="QR Code Pix" className="w-56 h-56 sm:w-64 sm:h-64 mx-auto rounded-lg border" data-testid="mp-pix-qr-img"/>
                ) : (
                  <div className="p-6 bg-slate-50 rounded-xl text-sm text-slate-500">QR não disponível — use o copia-e-cola abaixo</div>
                )}
                {plan && (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
                    <div className="text-xs uppercase text-slate-500">Valor a receber</div>
                    <div className="font-heading text-3xl font-black text-rose-600">R$ {Number(mpCharge.amount || plan.price).toFixed(2)}</div>
                    <div className="text-xs text-slate-500 mt-1">{plan.name}</div>
                  </div>
                )}
                {mpCharge.qr_code && (
                  <div className="space-y-1">
                    <Input readOnly value={mpCharge.qr_code} className="font-mono text-xs text-center" data-testid="mp-pix-copy-input"/>
                    <Button variant="outline" size="sm" onClick={()=>{ navigator.clipboard.writeText(mpCharge.qr_code); toast.success("Código Pix copiado"); }} data-testid="mp-pix-copy-btn" className="w-full"><Copy className="w-4 h-4 mr-2"/>Copiar Pix copia-e-cola</Button>
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>
                  <span>Aguardando pagamento... (atualiza automaticamente)</span>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" onClick={cancelMpPix} className="flex-1" data-testid="mp-pix-cancel">Cancelar cobrança</Button>
                  <Button onClick={forceMpSync} disabled={mpSyncing} className="flex-1 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="mp-pix-sync"><CheckCircle2 className="w-4 h-4 mr-2"/>{mpSyncing ? "Consultando..." : "Já paguei — conferir"}</Button>
                </div>
                <p className="text-[11px] text-slate-400">Ref: {mpCharge.external_reference}</p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Anonymous sale dialog */}
      <Dialog open={anonOpen} onOpenChange={(v)=>{ if (!v) closeAnon(); else setAnonOpen(v); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[95vh] overflow-y-auto" data-testid="quicksale-anon-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserX className="w-5 h-5 text-slate-700"/>Venda sem cadastro</DialogTitle></DialogHeader>
          {anonResult ? (
            <div className="text-center space-y-3 py-2">
              <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto"/>
              <h3 className="font-heading text-xl font-bold text-slate-900">Venda registrada!</h3>
              <p className="text-sm text-slate-600">Aparece no Caixa do Dia como <strong>Sem cadastro</strong></p>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-left">
                <div className="text-xs text-slate-500 uppercase tracking-wider">Plano</div>
                <div className="font-heading text-lg font-bold text-slate-900">{anonResult.plan.name}</div>
                <div className="font-heading text-2xl font-black text-emerald-600 mt-1">R$ {anonResult.plan.price.toFixed(2)}</div>
              </div>
              <Button
                onClick={releaseAnonTurnstile}
                disabled={anonReleasing || anonReleased}
                data-testid="quicksale-anon-release-turnstile"
                className={`w-full h-14 font-heading font-black uppercase tracking-wider text-white ${anonReleased ? "bg-emerald-600 hover:bg-emerald-600" : "bg-amber-600 hover:bg-amber-700"}`}>
                {anonReleased ? (<><CheckCircle2 className="w-5 h-5 mr-2"/>Catraca liberada</>) : (<><DoorOpen className="w-5 h-5 mr-2"/>{anonReleasing ? "Liberando..." : "Liberar acesso na catraca"}</>)}
              </Button>
              <p className="text-[11px] text-slate-500 -mt-1">Uma passagem única fica registrada no relatório de <strong>Liberação Geral</strong> vinculada a esta venda.</p>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={printAnonReceipt} className="bg-slate-900 hover:bg-black text-white font-bold uppercase" data-testid="quicksale-anon-print"><Printer className="w-4 h-4 mr-2"/>Recibo 80mm</Button>
                <Button onClick={closeAnon} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="quicksale-anon-new">Nova venda</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded px-3 py-2 leading-relaxed">
                ⚠️ Sem CPF, endereço ou facial. A venda aparece no <strong>Caixa do Dia</strong> como <strong>"Sem cadastro"</strong>. Ideal para passe único / day pass no balcão.
              </p>
              <div>
                <Label>Plano diário *</Label>
                <Select value={anonPlan} onValueChange={setAnonPlan}>
                  <SelectTrigger data-testid="anon-plan-select"><SelectValue placeholder="Selecionar plano diário"/></SelectTrigger>
                  <SelectContent>
                    {plans.filter(p => p.duration_days && p.duration_days > 0).map(p => <SelectItem key={p.id} value={p.id}>{p.name} — R$ {p.price.toFixed(2)} ({p.duration_days} dia{p.duration_days > 1 ? "s" : ""})</SelectItem>)}
                    {plans.filter(p => p.duration_days && p.duration_days > 0).length === 0 && (
                      <div className="px-3 py-4 text-xs text-slate-500 text-center">Nenhum plano diário cadastrado. Crie um em <strong>Planos</strong> definindo <strong>Dias = 1</strong>.</div>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1.5">Somente planos com duração em dias (passe único / day pass) aparecem aqui.</p>
              </div>
              <div>
                <Label>Forma de pagamento</Label>
                <Select value={anonMethod} onValueChange={setAnonMethod}>
                  <SelectTrigger data-testid="anon-method-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Dinheiro</SelectItem>
                    <SelectItem value="card">Cartão (máquina)</SelectItem>
                    <SelectItem value="pix_maquineta">Pix na Maquineta</SelectItem>
                    <SelectItem value="pix_qrcode">Pix com QR Code na Tela</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {anonPlan && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Total</div>
                  <div className="font-heading text-3xl font-black text-slate-900">R$ {plans.find(p => p.id === anonPlan)?.price.toFixed(2) || "0,00"}</div>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={closeAnon} className="flex-1">Cancelar</Button>
                <Button onClick={submitAnonymous} disabled={anonLoading || !anonPlan} className="flex-1 bg-slate-900 hover:bg-black text-white font-bold uppercase" data-testid="anon-submit">
                  {anonLoading ? "Registrando..." : anonMethod === "pix_qrcode" ? "Gerar QR Pix MP" : "Registrar venda"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pix Mercado Pago para venda sem cadastro */}
      {anonPlan && (
        <MpPixDialog
          open={anonMpPixOpen}
          onOpenChange={setAnonMpPixOpen}
          plan={plans.find(p => p.id === anonPlan)}
          createPayload={{ plan_id: anonPlan, anonymous: true }}
          endpoints={{ create: "/payments/mp/pix/create", status: "/payments/mp/status/", sync: "/payments/mp/sync/" }}
          auth={api}
          onPaid={onAnonMpPaid}
          title="Pix Mercado Pago — Venda sem cadastro"
        />
      )}
    </div>
  );
}
